/**
 * Main Chat Assistant drawer component
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { ChatService } from '../../services/chatService';
import { ChatMessage as ChatMessageType, TransactionConfirmation } from '../../types/chat';

interface ChatAssistantProps {
  open: boolean;
  onClose: () => void;
}

const WELCOME_MESSAGE: ChatMessageType = {
  id: 'welcome',
  role: 'assistant',
  content:
    "Hello! I'm your Trade Assistant. I can help you record trades, exits, and adjustments using natural language.\n\nTry saying something like:\n- \"I exited LCII at $138.07 in my Market Surge portfolio\"\n- \"Buy 50 shares of AAPL at $185 in Main Portfolio\"\n- \"Raise stop loss on NVDA to $120 in Growth Portfolio\"",
  timestamp: new Date(),
};

const ChatAssistant: React.FC<ChatAssistantProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [messages, setMessages] = useState<ChatMessageType[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = useState<TransactionConfirmation | null>(null);
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      // Build conversation history (exclude welcome message)
      const history = messages
        .filter((m) => m.id !== 'welcome')
        .map((m) => ({ role: m.role, content: m.content }));

      const response = await ChatService.sendMessage(input.trim(), history);

      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.content,
        timestamp: new Date(),
        confirmation: response.confirmation,
      };

      setMessages((prev) => [...prev, assistantMessage]);

      if (response.confirmation) {
        setPendingConfirmation(response.confirmation);
        setPendingMessageId(assistantMessage.id);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'An error occurred. Please try again.'
      );

      const errorMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!pendingConfirmation) return;

    setIsConfirming(true);
    setError(null);

    try {
      const result = await ChatService.executeTransaction(pendingConfirmation);

      // Add success message
      const successMessage: ChatMessageType = {
        id: Date.now().toString(),
        role: 'assistant',
        content: result.message,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, successMessage]);
      setPendingConfirmation(null);
      setPendingMessageId(null);
    } catch (err) {
      console.error('Transaction execution error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to execute transaction. Please try again.'
      );

      const errorMessage: ChatMessageType = {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'Sorry, I was unable to execute the transaction. Please try again or use the regular interface.',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancel = () => {
    setPendingConfirmation(null);
    setPendingMessageId(null);

    const cancelMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: 'assistant',
      content: 'Transaction cancelled. Is there anything else I can help you with?',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, cancelMessage]);
  };

  const handleClearChat = () => {
    setMessages([WELCOME_MESSAGE]);
    setPendingConfirmation(null);
    setPendingMessageId(null);
    setError(null);
  };

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMobile ? '100%' : 420,
          height: isMobile ? '90vh' : '100%',
          borderRadius: isMobile ? '16px 16px 0 0' : 0,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Trade Assistant
        </Typography>
        <Box>
          <IconButton
            onClick={handleClearChat}
            sx={{ color: 'white', mr: 1 }}
            size="small"
            title="Clear chat"
          >
            <Typography variant="caption">Clear</Typography>
          </IconButton>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 1 }}>
          {error}
        </Alert>
      )}

      {/* Messages */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 2,
          bgcolor: 'grey.50',
        }}
      >
        {messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            message={msg}
            onConfirm={
              msg.id === pendingMessageId && pendingConfirmation
                ? handleConfirm
                : undefined
            }
            onCancel={
              msg.id === pendingMessageId && pendingConfirmation
                ? handleCancel
                : undefined
            }
            isConfirming={isConfirming && msg.id === pendingMessageId}
          />
        ))}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                bgcolor: 'grey.100',
                borderRadius: 2,
                borderTopLeftRadius: 0,
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Thinking...
              </Typography>
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <ChatInput
        value={input}
        onChange={setInput}
        onSend={handleSend}
        disabled={isLoading || isConfirming}
        placeholder="Record a trade, exit, or adjustment..."
      />
    </Drawer>
  );
};

export default ChatAssistant;
